import React, { useState, useEffect } from 'react';
import './FishboneDiagramVisual.css';

const FishboneDiagramVisual = ({ onDataChange, initialData, isVisible = true }) => {
  const [fishboneData, setFishboneData] = useState({
    problem: '',
    causes: {
      methode: [],
      mainDoeuvre: [],
      machine: [],
      matiere: [],
      milieu: [],
      mesure: []
    }
  });

  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    console.log('🐟 FishboneDiagramVisual - initialData reçue:', initialData);
    if (initialData && typeof initialData === 'object') {
      console.log('🐟 FishboneDiagramVisual - Type objet détecté, traitement...');
      // Vérifier la structure des données et adapter si nécessaire
      if (initialData.categories && !initialData.causes) {
        console.log('🔄 FishboneDiagramVisual - Adaptation ancien format vers nouveau');
        // Adapter l'ancien format au nouveau format
        const adaptedData = {
          problem: initialData.problem || '',
          causes: {
            methode: initialData.categories['Méthode'] || [],
            mainDoeuvre: initialData.categories['Main-d\'œuvre'] || [],
            machine: initialData.categories['Matériel'] || [],
            matiere: initialData.categories['Matière'] || [],
            milieu: initialData.categories['Milieu'] || [],
            mesure: initialData.categories['Mesure'] || []
          }
        };
        setFishboneData(adaptedData);
        console.log('✅ FishboneDiagramVisual - Données adaptées:', adaptedData);
      } else if (initialData.causes) {
        console.log('✅ FishboneDiagramVisual - Format correct, chargement direct');
        // Format déjà correct
        setFishboneData(initialData);
      }
    } else {
      console.log('🐟 FishboneDiagramVisual - Aucune initialData valide');
    }
  }, [initialData]);

  const updateData = (newData) => {
    setFishboneData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
  };

  const handleProblemChange = (value) => {
    const newData = { ...fishboneData, problem: value };
    updateData(newData);
  };

  const addCause = (category) => {
    const newData = { ...fishboneData };
    
    // Limitation à 7 causes maximum par branche
    if (newData.causes[category].length >= 7) {
      alert(`🚫 Maximum 7 causes par branche atteint pour ${getCategoryLabel(category)} !`);
      return;
    }
    
    const newCause = prompt(`Ajouter une cause pour ${getCategoryLabel(category)} (${newData.causes[category].length + 1}/7):`);
    if (newCause && newCause.trim()) {
      newData.causes[category].push(newCause.trim());
      updateData(newData);
    }
  };

  const editCause = (category, index) => {
    const currentCause = fishboneData.causes[category][index];
    const newCause = prompt(`Modifier la cause:`, currentCause);
    if (newCause !== null) {
      const newData = { ...fishboneData };
      if (newCause.trim()) {
        newData.causes[category][index] = newCause.trim();
      } else {
        newData.causes[category].splice(index, 1);
      }
      updateData(newData);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      methode: 'Méthode',
      mainDoeuvre: 'Main-d\'œuvre',
      machine: 'Machine',
      matiere: 'Matière',
      milieu: 'Milieu',
      mesure: 'Mesure'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      methode: '#E57373',
      mainDoeuvre: '#81C784',
      machine: '#64B5F6',
      matiere: '#FFB74D',
      milieu: '#BA68C8',
      mesure: '#4DB6AC'
    };
    return colors[category] || '#E0E0E0';
  };

  const updateCause = (category, index, value) => {
    const newData = { ...fishboneData };
    newData.causes[category][index] = value;
    setFishboneData(newData);
  };

  const removeCause = (category, index) => {
    const newData = { ...fishboneData };
    newData.causes[category].splice(index, 1);
    setFishboneData(newData);
  };


  if (!isVisible) return null;

  return (
    <div className="fishbone-container">
      <div className="fishbone-header">
        <h3>🐟 Diagramme de Fishbone (Ishikawa)</h3>
        <div className="fishbone-controls">
          <button 
            className={`edit-mode-btn ${editMode ? 'active' : ''}`}
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? '👁️ Visualiser' : '✏️ Éditer'}
          </button>
        </div>
      </div>

      <div className="fishbone-diagram">
        <svg width="1200" height="700" viewBox="0 0 1200 700" className="fishbone-svg">
          {/* Épine dorsale principale */}
          <line x1="150" y1="350" x2="950" y2="350" stroke="#8B4513" strokeWidth="4" />
          
          {/* Queue du poisson (à gauche, derrière) */}
          <path 
            d="M 150 350 
               L 80 320
               L 80 350
               L 80 380
               L 150 350" 
            fill="#FFD700" 
            stroke="#DAA520" 
            strokeWidth="2"
          />
          
          {/* Tête de poisson stylisée (à droite, devant) */}
          <path 
            d="M 950 350 
               C 1000 320, 1050 320, 1100 350
               C 1050 380, 1000 380, 950 350 Z" 
            fill="#FFD700" 
            stroke="#DAA520" 
            strokeWidth="2"
          />
          
          {/* Œil du poisson */}
          <circle cx="1030" cy="350" r="6" fill="#000" />

          {/* Branches principales - 1 et 2 opposées reculées */}
          
          {/* 01 - Méthode (haut) - Reculée */}
          <line x1="250" y1="350" x2="200" y2="150" stroke="#E57373" strokeWidth="3" />
          <circle cx="225" cy="250" r="20" fill="#E57373" stroke="#D32F2F" strokeWidth="2" />
          <text x="225" y="255" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">01</text>
          
          {/* 02 - Main-d'œuvre (bas - opposée à 01) - Reculée */}
          <line x1="250" y1="350" x2="200" y2="550" stroke="#81C784" strokeWidth="3" />
          <circle cx="225" cy="450" r="20" fill="#81C784" stroke="#388E3C" strokeWidth="2" />
          <text x="225" y="455" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">02</text>
          
          {/* 03 - Machine (haut) */}
          <line x1="590" y1="350" x2="540" y2="150" stroke="#64B5F6" strokeWidth="3" />
          <circle cx="565" cy="250" r="20" fill="#64B5F6" stroke="#1976D2" strokeWidth="2" />
          <text x="565" y="255" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">03</text>
          
          {/* 04 - Matière (bas - opposée à 03) */}
          <line x1="590" y1="350" x2="540" y2="550" stroke="#FFB74D" strokeWidth="3" />
          <circle cx="565" cy="450" r="20" fill="#FFB74D" stroke="#F57C00" strokeWidth="2" />
          <text x="565" y="455" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">04</text>
          
          {/* 05 - Milieu (bas gauche - même sens que branche 4) */}
          <line x1="910" y1="350" x2="860" y2="550" stroke="#BA68C8" strokeWidth="3" />
          <circle cx="885" cy="450" r="20" fill="#BA68C8" stroke="#7B1FA2" strokeWidth="2" />
          <text x="885" y="455" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">05</text>
          
          {/* 06 - Mesure (haut - opposée à 05) */}
          <line x1="910" y1="350" x2="860" y2="150" stroke="#4DB6AC" strokeWidth="3" />
          <circle cx="885" cy="250" r="20" fill="#4DB6AC" stroke="#00695C" strokeWidth="2" />
          <text x="885" y="255" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">06</text>

          {/* Lignes de causes - 1 et 2 opposées */}
          {Object.entries(fishboneData.causes || {}).map(([ category, causes], categoryIndex) => {
            const positions = [
              { x: 200, y: 150, isTop: true, direction: 'left' },   // Méthode (01 - haut, reculée)
              { x: 200, y: 550, isTop: false, direction: 'left' },  // Main-d'œuvre (02 - bas, reculée)
              { x: 540, y: 150, isTop: true, direction: 'left' },   // Machine (03 - haut)
              { x: 540, y: 550, isTop: false, direction: 'left' },  // Matière (04 - bas, opposée à 03)
              { x: 860, y: 550, isTop: false, direction: 'left' },  // Milieu (05 - bas gauche, même sens que 04)
              { x: 860, y: 150, isTop: true, direction: 'left' }    // Mesure (06 - haut, opposée à 05)
            ];
            
            const pos = positions[categoryIndex];
            if (!pos) return null;
            
            return (causes || []).map((cause, causeIndex) => {
              // Chaque cause a sa propre position sur la branche principale
              const lineLength = 120; 
              const spacingAlongBranch = 40; // Espacement le long de la branche principale
              
              // Position de départ sur la branche principale (échelonnée)
              const branchStartX = pos.x;
              const branchStartY = pos.y;
              
              // Direction de la branche principale vers l'épine dorsale
              const toSpineX = pos.direction === 'left' ? 50 : -50; // Distance vers l'épine
              const toSpineY = pos.isTop ? 200 : -200; // Distance vers l'épine (haut/bas)
              
              // Position échelonnée sur la branche principale
              const branchOffsetRatio = causeIndex * 0.15; // 15% plus loin sur la branche pour chaque cause
              const startX = branchStartX + (toSpineX * branchOffsetRatio);
              const startY = branchStartY + (toSpineY * branchOffsetRatio);
              
              // Position finale de la cause (perpendiculaire à la branche)
              const directionMultiplier = pos.direction === 'left' ? -1 : 1;
              const causeX = startX + (lineLength * directionMultiplier);
              const causeY = startY;
              
              return (
                <g key={`${category}-${causeIndex}`}>
                  <line 
                    x1={startX} 
                    y1={startY} 
                    x2={causeX} 
                    y2={causeY} 
                    stroke="#666" 
                    strokeWidth="2" 
                  />
                  <circle cx={causeX} cy={causeY} r="4" fill="#666" />
                  <text 
                    x={causeX + (directionMultiplier * 15)} 
                    y={causeY + 4} 
                    fontSize="12" 
                    fill="#333"
                    fontWeight="500"
                    textAnchor={pos.direction === 'left' ? "end" : "start"}
                  >
                    {cause}
                  </text>
                </g>
              );
            });
          })}
        </svg>

        {/* Étiquettes des catégories - 1 et 2 opposées reculées */}
        <div className="category-labels">
          {/* 01 - Méthode (haut) - Rapprochée de sa branche */}
          <div className="content-label content-01" style={{position: 'absolute', top: '120px', left: '180px'}}>
            <span>MÉTHODE</span>
          </div>
          
          {/* 02 - Main-d'œuvre (bas) - Rapprochée de sa branche */}
          <div className="content-label content-02" style={{position: 'absolute', top: '540px', left: '180px'}}>
            <span>MAIN-D'ŒUVRE</span>
          </div>
          
          {/* 03 - Machine (rapprochée de sa branche comme MÉTHODE) */}
          <div className="content-label content-03" style={{position: 'absolute', top: '120px', left: '520px'}}>
            <span>MACHINE</span>
          </div>
          
          {/* 04 - Matière (bas - opposée à 03) */}
          <div className="content-label content-04" style={{position: 'absolute', top: '540px', left: '520px'}}>
            <span>MATIÈRE</span>
          </div>
          
          {/* 05 - Milieu (bas gauche - même sens que MATIÈRE) */}
          <div className="content-label content-05" style={{position: 'absolute', top: '540px', left: '840px'}}>
            <span>MILIEU</span>
          </div>
          
          {/* 06 - Mesure (haut - opposée à 05) */}
          <div className="content-label content-06" style={{position: 'absolute', top: '120px', left: '840px'}}>
            <span>MESURE</span>
          </div>
        </div>

        {/* Zone du problème principal - Bouton simple ou carré d'édition */}
        {editMode ? (
          <div className="problem-area">
            <textarea
              value={fishboneData.problem}
              onChange={(e) => handleProblemChange(e.target.value)}
              placeholder="Problème principal"
              className="problem-input"
              rows="6"
            />
          </div>
        ) : (
          <div className="problem-button-area">
            <button 
              className="problem-button"
              onClick={() => setEditMode(true)}
              title="Cliquer pour définir le problème principal"
            >
              📝 Définir le problème
            </button>
          </div>
        )}

        {/* Interface d'édition des causes */}
        {editMode && (
          <div className="edit-panel">
            <h4>Éditer les causes</h4>
            {Object.entries(fishboneData.causes || {}).map(([category, causes], categoryIndex) => {
              const categoryLabels = {
                methode: 'MÉTHODE',
                mainDoeuvre: 'MAIN-D\'ŒUVRE', 
                machine: 'MACHINE',
                matiere: 'MATIÈRE',
                milieu: 'MILIEU',
                mesure: 'MESURE'
              };
              
              return (
                <div key={category} className="edit-category">
                  <h5>{categoryLabels[category]}</h5>
                  {causes.map((cause, index) => (
                    <div key={index} className="edit-cause-item">
                      <input
                        type="text"
                        value={cause}
                        onChange={(e) => updateCause(category, index, e.target.value)}
                        placeholder="Saisir une cause..."
                      />
                      <button onClick={() => removeCause(category, index)}>❌</button>
                    </div>
                  ))}
                  <button 
                    onClick={() => addCause(category)} 
                    className="add-cause-btn"
                    disabled={causes.length >= 7}
                    title={causes.length >= 7 ? "Maximum 7 causes par branche" : `Ajouter une cause (${causes.length}/7)`}
                  >
                    {causes.length >= 7 ? '🚫 Limite atteinte (7/7)' : `+ Ajouter une cause (${causes.length}/7)`}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="fishbone-legend">
        <p><strong>Instructions :</strong></p>
        <ul>
          <li>Cliquez sur "✏️ Éditer" pour modifier le diagramme</li>
          <li>Ajoutez des causes dans chaque catégorie (6M : Méthode, Main-d'œuvre, Machine, Matière, Milieu, Mesure)</li>
          <li>Cliquez sur une cause existante pour la modifier</li>
          <li>Utilisez "👁️ Visualiser" pour voir le diagramme final</li>
        </ul>
      </div>
    </div>
  );
};

export default FishboneDiagramVisual;
